import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Zap, Share2, ExternalLink,
  Package, Search, Clock, Wrench, CheckCircle2,
  RefreshCw, Truck, Star, XCircle, Check, User,
} from "lucide-react";
import { formatDate } from "@/utils/formatDate";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets-lqy194kg.emergentagent.net/job_charger-service-hub-1/artifacts/1jv6mr6c_2.jpeg";

// Company contact info
const COMPANY = {
  phone1: "+91 9871365888",
  phone2: "+91 8800562266",
  email: "xstrongpowerservice@gmail.com",
  hours: "Mon-Sat: 10:00 AM – 6:00 PM",
};

// ─── JOURNEY (UNCHANGED) ──────────────────────────────────────────────────────
const JOURNEY = [
  { key: "Received",               Icon: Package,       title: "Device Received",      desc: "Your charger has arrived at our service centre" },
  { key: "Under Investigation",    Icon: Search,        title: "Under Inspection",      desc: "Our technician is diagnosing the issue" },
  { key: "Waiting for Approval",   Icon: Clock,         title: "Awaiting Approval",     desc: "Service estimate ready — awaiting your confirmation" },
  { key: "Repair in Progress",     Icon: Wrench,        title: "Repair in Progress",    desc: "Your charger is being repaired by our expert" },
  { key: "Repaired",               Icon: CheckCircle2,  title: "Repair Completed",      desc: "Your charger has been successfully repaired" },
  { key: "Replacement Approved",   Icon: RefreshCw,     title: "Replacement Approved",  desc: "Replacement unit confirmed and being processed" },
  { key: "Replacement Dispatched", Icon: Truck,         title: "Dispatched",            desc: "Your device is on the way back to you" },
  { key: "Completed",              Icon: Star,          title: "Delivered",             desc: "Service completed. Thank you for choosing us!" },
  { key: "Warranty Rejected",      Icon: XCircle,       title: "Warranty Rejected",     desc: "Warranty claim could not be approved" },
];

// ─── Barcode SVG (UNCHANGED) ─────────────────────────────────────────────────
function Barcode({ ticketId }) {
  const str = (ticketId || "").replace(/[^A-Z0-9]/g, "");
  const bars = Array.from({ length: 52 }, (_, i) => {
    const c = str.charCodeAt(i % str.length) || 65;
    return { w: 2 + ((c * 3 + i * 7) % 3), h: 22 + ((c * 11 + i * 13) % 24), gap: 1 + ((c + i) % 2) };
  });
  let x = 0;
  const rects = bars.map((b) => { const r = { x, w: b.w, h: b.h }; x += b.w + b.gap; return r; });
  return (
    <div className="text-center">
      <svg width={Math.min(x, 200)} height="50" viewBox={`0 0 ${x} 50`} className="mx-auto">
        {rects.map((r, i) => <rect key={i} x={r.x} y={50 - r.h} width={r.w} height={r.h} fill="#111827" rx="0.5" />)}
      </svg>
      <p className="text-[11px] font-mono font-bold text-gray-800 tracking-widest mt-1">{ticketId}</p>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getChargers(ticket) {
  if (ticket.chargers && ticket.chargers.length > 0) return ticket.chargers;
  if (ticket.charger_model) return [{
    charger_model: ticket.charger_model || "",
    voltage: ticket.voltage || "",
    ampere: ticket.ampere || "",
    body_type: ticket.body_type || "",
    serial_number: ticket.serial_number || "",
    mfg_month: ticket.mfg_month || "",
    mfg_year: ticket.mfg_year || "",
    warranty_status: ticket.warranty_status || "",
  }];
  return [];
}

function statusBadgeClass(status) {
  if (!status) return "bg-gray-100 text-gray-600";
  if (status === "Completed") return "bg-green-100 text-green-700";
  if (status === "Warranty Rejected") return "bg-red-100 text-red-700";
  if (["Repair in Progress", "Repaired"].includes(status)) return "bg-lime-100 text-lime-700";
  if (status === "Waiting for Approval") return "bg-amber-100 text-amber-700";
  return "bg-blue-100 text-blue-700";
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CustomerTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState(id || "");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/public/track/${id}`)
      .then((r) => setTicket(r.data))
      .catch(() => setError("Ticket not found. Please check your ticket ID."))
      .finally(() => setLoading(false));
  }, [id]);

  const currentIdx = JOURNEY.findIndex((j) => j.key === ticket?.status);
  const effectiveIdx = currentIdx === -1 ? 0 : currentIdx;
  const progress = Math.round(((effectiveIdx + 1) / 9) * 100);
  const isRejected = ticket?.status === "Warranty Rejected";

  const trackingUrl = `${window.location.origin}/track/${ticket?.ticket_id || id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(trackingUrl)}&color=166534&bgcolor=ffffff&margin=4`;

  const historyMap = {};
  (ticket?.status_history || []).forEach((h) => { historyMap[h.status] = h; });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) navigate(`/track/${searchId.trim().toUpperCase()}`);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(trackingUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-gray-100" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Sticky header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="XSTRONG POWER" className="h-8 w-auto object-contain" />
            <div>
              <p className="font-black text-xs text-gray-900 tracking-wide uppercase leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>XSTRONG POWER</p>
              <p className="text-[10px] text-green-700 font-semibold">EV Charger Service</p>
            </div>
          </div>
          <a href="/login" className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Staff Portal
          </a>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 py-4 space-y-3">

        {/* Search */}
        {!id && (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
            <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-7 h-7 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Track Your Charger</h1>
            <p className="text-gray-500 text-sm mb-5">Enter your service ticket ID to check repair status</p>
            <form onSubmit={handleSearch} className="flex gap-2 max-w-xs mx-auto">
              <input value={searchId} onChange={(e) => setSearchId(e.target.value.toUpperCase())} placeholder="SVC-2026-0001"
                data-testid="tracking-search-input"
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-gray-800 bg-gray-50" />
              <button type="submit" data-testid="tracking-search-btn"
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-colors">Track</button>
            </form>
          </div>
        )}

        {loading && id && (
          <div className="animate-pulse space-y-3">
            <div className="h-36 bg-white rounded-2xl" />
            <div className="h-24 bg-white rounded-2xl" />
          </div>
        )}

        {error && (
          <div className="bg-white border border-red-100 rounded-2xl p-8 text-center">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-700 font-semibold text-sm">{error}</p>
            <button onClick={() => navigate("/track")} className="mt-3 text-xs text-red-500 hover:underline font-medium">Try another ID</button>
          </div>
        )}

        {ticket && (
          <>
            {/* ── COMPANY HEADER CARD ── */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="p-4 flex items-start justify-between gap-3">
                {/* Left: Logo + company */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <img src={LOGO} alt="XSTRONG POWER" className="h-14 w-auto object-contain flex-shrink-0" />
                  <div className="min-w-0">
                    <h1 className="font-black text-green-600 text-base leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      XSTRONG POWER EV CHARGER
                    </h1>
                    <p className="text-xs font-bold text-gray-700 mt-0.5">EV Charger Repair & Service Center</p>
                    <p className="text-[11px] text-gray-500 mt-1">📞 {COMPANY.phone1} / {COMPANY.phone2}</p>
                    <p className="text-[11px] text-gray-500">✉ {COMPANY.email}</p>
                    <p className="text-[11px] text-gray-500">🕐 {COMPANY.hours}</p>
                  </div>
                </div>
                {/* Right: Ticket ID + date + status */}
                <div className="text-right flex-shrink-0">
                  <p className="font-black text-xl text-gray-900 font-mono" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {ticket.ticket_id}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Date: {formatDate(ticket.received_date)}</p>
                  <span className={`inline-block mt-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full ${statusBadgeClass(ticket.status)}`}>
                    🏷 {ticket.status}
                  </span>
                </div>
              </div>
              {/* Green accent line */}
              <div className="h-0.5 bg-gradient-to-r from-green-500 to-emerald-400" />

              {/* Barcode + QR section */}
              <div className="p-4 flex items-center gap-4 border-t border-gray-50">
                <div className="flex-shrink-0">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Shipment Barcode</p>
                  <div className="bg-gray-50 rounded-xl p-2.5 border border-gray-100">
                    <Barcode ticketId={ticket.ticket_id} />
                  </div>
                </div>
                <div className="flex-shrink-0 text-center">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">QR — Customer Track</p>
                  <div className="p-1.5 bg-white border border-gray-100 rounded-xl shadow-sm inline-block">
                    <img src={qrUrl} alt="QR" className="w-16 h-16" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Scan QR to track repair status online</p>
                  <p className="text-xs text-gray-400 mt-0.5">Attach this barcode to the device/packaging</p>
                  <button onClick={copyLink} data-testid="copy-tracking-link"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                    <Share2 className="w-3 h-3" />
                    {copied ? "Copied!" : "Share Link"}
                  </button>
                </div>
              </div>
            </div>

            {/* ── CUSTOMER DETAILS ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Customer Details</p>
              </div>
              <div className="p-4 space-y-2.5 text-sm">
                {[
                  { label: "Name", value: ticket.customer_name },
                  { label: "Phone", value: ticket.phone },
                  { label: "Alt Phone", value: ticket.alternate_phone },
                  { label: "Address", value: [ticket.address, ticket.city, ticket.state, ticket.pin_code].filter(Boolean).join(", ") },
                ].filter(r => r.value).map(({ label, value }) => (
                  <div key={label} className="flex gap-4">
                    <span className="text-gray-400 w-20 flex-shrink-0 text-xs pt-0.5">{label}:</span>
                    <span className="text-gray-900 font-semibold leading-snug">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── CHARGER DETAILS TABLE ── */}
            {getChargers(ticket).length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Charger Details ({getChargers(ticket).length} Unit{getChargers(ticket).length !== 1 ? "s" : ""})
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400 w-8">#</th>
                        <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400">Battery Cut-Off / Ampere</th>
                        <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400">Serial Number</th>
                        <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400">Purchase Date</th>
                        <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-400">Body Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getChargers(ticket).map((ch, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-3 font-bold text-green-600">
                            {[ch.voltage, ch.ampere].filter(Boolean).join(" / ") || "—"}
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-700 text-xs">{ch.serial_number || "—"}</td>
                          <td className="px-4 py-3 text-gray-700">{[ch.mfg_month, ch.mfg_year].filter(Boolean).join(" ") || "—"}</td>
                          <td className="px-4 py-3 text-gray-700">{ch.body_type || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── XP CHARGER STATUS ── */}
            {ticket.xp_charger_status && (
              <div className={`rounded-2xl border-2 p-4 shadow-sm overflow-hidden ${
                ticket.xp_charger_status === "returned"
                  ? "bg-orange-50 border-orange-400"
                  : "bg-green-50 border-green-500"
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    ticket.xp_charger_status === "returned" ? "bg-orange-100" : "bg-green-100"
                  }`}>
                    <span className="text-xl">
                      {ticket.xp_charger_status === "returned" ? "🔄" : "✅"}
                    </span>
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 ${
                      ticket.xp_charger_status === "returned" ? "text-orange-500" : "text-green-600"
                    }`}>
                      XP Chargers
                    </p>
                    <p className={`text-sm font-black ${
                      ticket.xp_charger_status === "returned" ? "text-orange-800" : "text-green-900"
                    }`}>
                      {ticket.xp_charger_status === "returned"
                        ? "Returned to Service Centre"
                        : "New Charger Given to Customer"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── FAULT & SPECIFICATIONS ── */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Fault & Specifications</p>
              </div>
              <div className="p-4 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-semibold">Fault Description</p>
                  <p className="text-gray-800 leading-relaxed">{ticket.fault_description || <span className="text-gray-300 italic">Not specified</span>}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-semibold">Technical Notes</p>
                  <p className="text-gray-800 leading-relaxed">{ticket.repair_notes || <span className="text-gray-300 italic">Not specified</span>}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-semibold">Repair Notes</p>
                  <p className="text-gray-800 leading-relaxed">{ticket.repair_notes || <span className="text-gray-300 italic">Not specified</span>}</p>
                </div>
              </div>
            </div>

            {/* ── COURIER / DISPATCH DETAILS ── */}
            {(ticket.courier_name || ticket.dispatch_from || ticket.dispatch_awb || ticket.tracking_link) && (
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4 shadow-sm">
                <p className="text-xs font-bold text-blue-700 mb-3 flex items-center gap-1.5 uppercase tracking-wider">
                  🚚 Courier / Dispatch Details
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
                  {(ticket.courier_name || ticket.dispatch_from) && (
                    <p className="text-gray-600">Courier Service: <strong className="text-gray-900">{ticket.courier_name || ticket.dispatch_from}</strong></p>
                  )}
                  {ticket.dispatch_awb && (
                    <p className="text-gray-600">AWB / Tracking No.: <strong className="text-blue-700 font-mono">{ticket.dispatch_awb}</strong></p>
                  )}
                </div>
                {ticket.tracking_link && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Track at:{" "}
                    <a href={ticket.tracking_link} target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium">{ticket.tracking_link}</a>
                  </p>
                )}
              </div>
            )}

            {/* ── SERVICE JOURNEY (UNCHANGED) ── */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Journey header */}
              <div className="px-6 pt-6 pb-4 border-b border-gray-50">
                <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Service Journey — Step {effectiveIdx + 1} of {JOURNEY.length}
                </h3>
                {/* Progress bar */}
                <div className="flex items-center justify-between text-xs text-gray-400 mb-2 mt-3">
                  <span className="font-semibold text-gray-600">{JOURNEY[0].title}</span>
                  <span className={`font-bold text-sm ${isRejected ? "text-red-600" : "text-green-700"}`}>
                    {progress}% complete
                  </span>
                  <span className="font-semibold text-gray-600">{JOURNEY[JOURNEY.length - 2].title}</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${isRejected ? "bg-gradient-to-r from-red-400 to-red-500" : "bg-gradient-to-r from-green-400 to-emerald-500"}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="px-6 py-4">
                {JOURNEY.map((step, idx) => {
                  const done = idx < effectiveIdx;
                  const current = idx === effectiveIdx;
                  const future = idx > effectiveIdx;
                  const histEntry = historyMap[step.key];
                  const isThisRejected = step.key === "Warranty Rejected" && current;

                  return (
                    <div key={step.key} className="flex gap-4">
                      {/* Icon + connector */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 mt-0.5 ${
                          done
                            ? "bg-green-500 shadow-md shadow-green-200"
                            : current && !isThisRejected
                            ? "bg-white border-2 border-green-500 shadow-lg shadow-green-100 ring-4 ring-green-50"
                            : isThisRejected
                            ? "bg-white border-2 border-red-400 shadow-lg shadow-red-100 ring-4 ring-red-50"
                            : "bg-gray-100"
                        }`}>
                          {done
                            ? <Check className="w-5 h-5 text-white stroke-[2.5]" />
                            : <step.Icon className={`w-5 h-5 ${
                                current && !isThisRejected ? "text-green-600"
                                : isThisRejected ? "text-red-500"
                                : future ? "text-gray-300"
                                : "text-gray-400"
                              }`} />
                          }
                        </div>
                        {idx < JOURNEY.length - 1 && (
                          <div className={`w-0.5 h-7 my-1 rounded-full transition-all ${done ? "bg-green-300" : "bg-gray-100"}`} />
                        )}
                      </div>

                      {/* Content */}
                      <div className={`pb-5 pt-1.5 flex-1 min-w-0 ${future ? "opacity-30" : ""}`}>
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className={`font-bold text-sm leading-tight ${
                            done ? "text-green-800"
                            : current && !isThisRejected ? "text-gray-900"
                            : isThisRejected ? "text-red-800"
                            : "text-gray-400"
                          }`}>
                            {step.title}
                          </span>
                          {current && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide ${
                              isThisRejected ? "bg-red-100 text-red-700" : "bg-green-100 text-green-800"
                            }`}>
                              CURRENT
                            </span>
                          )}
                          {histEntry && !future && (
                            <span className="text-[10px] text-gray-400 ml-auto font-medium whitespace-nowrap">
                              {histEntry.timestamp?.slice(0, 16).replace("T", " at ")}
                            </span>
                          )}
                        </div>

                        <p className={`text-xs mt-0.5 leading-relaxed ${
                          done ? "text-green-600" : current ? "text-gray-600" : "text-gray-400"
                        }`}>
                          {step.desc}
                        </p>

                        {current && histEntry?.notes && (
                          <div className="mt-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100 flex items-start gap-2">
                            <div className="w-4 h-4 bg-gray-200 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[9px]">📝</span>
                            </div>
                            <p className="text-xs text-gray-700">{histEntry.notes}</p>
                          </div>
                        )}
                        {done && histEntry?.notes && (
                          <p className="text-[11px] text-green-600 mt-1 italic">"{histEntry.notes}"</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-xs text-gray-400 pb-4 font-medium">
              For support: {COMPANY.email} · {COMPANY.phone1}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
